import { useMemo } from "react";
import { useLocation } from "react-router-dom";

/**
 * useFeatureFlag — R1a helper.
 *
 * Reads a feature-flag override from either the query string (`?ff=<key>=1`
 * or `?ff=<key>=0`) OR the caller-supplied `flags` map (populated from
 * `/api/feature-flags`). The query-string source only affects the current
 * session and is intended as the surgical R1a test path — production flips
 * come through the tenant flag stored server-side.
 *
 * @param {string} key
 * @param {Record<string, boolean>|undefined} flags — tenant feature flags map keyed by flag key.
 * @returns {boolean}
 */
export function useFeatureFlag(key, flags) {
  const location = useLocation();
  return useMemo(() => {
    // 1. URL override wins for R1a testing.
    const q = new URLSearchParams(location.search);
    const raw = q.get("ff");
    if (raw) {
      for (const pair of raw.split(",")) {
        const [k, v] = pair.split("=");
        if (k === key) return v === "1" || v === "true";
      }
    }
    // 2. Fall back to tenant flag map.
    if (flags && key in flags) return !!flags[key];
    return false;
  }, [key, flags, location.search]);
}
